import { z } from 'zod';
/** „Vom Kunden" (FZ-03): all seven done = documents complete (F2 gate for „Als angefragt vermerken"). */
export const customerChecklistItems = [
    { key: 'id_document', title: 'Personalausweis', hint: null },
    { key: 'salary_statements', title: 'Gehaltsnachweise 3 Monate', hint: null },
    { key: 'employment_contract', title: 'Arbeitsvertrag', hint: null },
    { key: 'self_disclosure', title: 'Selbstauskunft', hint: null },
    { key: 'tax_assessment', title: 'Steuerbescheid', hint: null },
    { key: 'bank_statements', title: 'Kontoauszüge 3 Monate', hint: 'alle Girokonten' },
    { key: 'equity_proof', title: 'Eigenkapitalnachweis', hint: 'Depot- und Sparkontoauszug' },
];
/** „Von Ihnen" (FZ-03): the financier's own papers; informational, never gating. */
export const financierChecklistItems = [
    { key: 'household_calculation', title: 'Haushaltsrechnung', hint: null },
    { key: 'financing_confirmation', title: 'Finanzierungsbestätigung', hint: 'nach Zusage' },
    {
        key: 'financing_contract',
        title: 'Finanzierungsvertrag unterschrieben',
        hint: 'Voraussetzung Notartermin',
    },
];
/** „zum Objekt" (FZ-05 „3 von 3 zum Objekt"): the broker's papers on the deal, its units or its property. */
export const objectChecklistKeys = [
    'reservation_agreement',
    'consultation_protocol',
    'expose',
];
export const financierChecklistStates = ['done', 'requested', 'open'];
/** Offer fields the extraction reads and the financier confirms (FI10, Finanzierer 06/10). */
export const offerFieldKeys = [
    'bank',
    'loanCents',
    'nominalRatePct',
    'effectiveRatePct',
    'fixedInterestYears',
    'installmentCents',
    'repaymentPct',
    'expiresOn',
    'availableFromOn',
    'forwardPremiumPct',
];
/** Field labels of „Angebot prüfen" (06), „Eckdaten selbst eintragen" (10) and „Konditionen der Zusage" (F-06). */
export const offerFieldLabels = {
    bank: 'Bank',
    loanCents: 'Darlehen',
    nominalRatePct: 'Sollzins',
    effectiveRatePct: 'Effektivzins',
    fixedInterestYears: 'Zinsbindung',
    installmentCents: 'Rate',
    repaymentPct: 'Tilgung',
    expiresOn: 'Gültig bis',
    availableFromOn: 'Bereitstellungszins',
    forwardPremiumPct: 'Forward-Aufschlag',
};
/** FI12: mandatory when an offer is entered or confirmed (bank besides; „Sollzins, Zinsbindung und Gültigkeit sind Pflicht"). */
export const mandatoryOfferFields = [
    'bank',
    'nominalRatePct',
    'fixedInterestYears',
    'expiresOn',
];
export const offerStatuses = ['draft', 'offer', 'chosen', 'rejected', 'expired', 'replaced'];
export const offerUploadStates = ['pending', 'unreadable', 'unavailable', 'failed'];
export const signatureMethods = ['digital', 'offline'];
/** Segments of „Wie wurde unterschrieben?" (FZ-07). */
export const signatureMethodLabels = {
    digital: 'Digital über die Bank',
    offline: 'Offline auf Papier',
};
/** Offer PDFs (FI12): „Angenommen werden PDF-Dateien bis 20 MB." */
export const OFFER_MAX_MB = 20;
const REQUIRED = 'Pflichtangabe.';
const dayString = z.string({ error: REQUIRED }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte ein Datum wählen.');
const text = (max) => z.string({ error: REQUIRED }).trim().max(max, `Höchstens ${max} Zeichen.`);
const requiredText = (max) => text(max).min(1, REQUIRED);
const cents = z
    .number({ error: 'Bitte einen Betrag angeben.' })
    .int('Bitte einen Betrag in Cent angeben.')
    .min(0, 'Der Betrag darf nicht negativ sein.')
    .max(100_000_000_000, 'Der Betrag ist zu hoch.');
const ratePct = z
    .number({ error: 'Bitte einen Prozentsatz angeben.' })
    .min(0, 'Der Prozentsatz darf nicht negativ sein.')
    .max(100, 'Höchstens 100 %.');
const years = z
    .number({ error: 'Bitte die Zinsbindung in Jahren angeben.' })
    .min(0, 'Die Zinsbindung darf nicht negativ sein.')
    .max(50, 'Höchstens 50 Jahre.');
/** POST /financings/:id/document-requests (FZ-04). */
export const financierDocumentRequestBodySchema = z.object({
    items: z
        .array(z.object({ documentTypeId: z.string().uuid().optional(), title: requiredText(200) }), {
        error: REQUIRED,
    })
        .min(1, 'Bitte wählen Sie mindestens eine Unterlage.')
        .max(20, 'Höchstens 20 Unterlagen je Anforderung.'),
    neededByOn: dayString,
    /** FZ-04 names the client portal; it exists from M7 on — until then `portal` is answered 409. */
    channel: z.enum(['email', 'portal']).optional(),
    copyToId: z.string().uuid().nullable().optional(),
    message: text(2000).optional(),
});
/** PUT /financings/:id/request-note (FZ-05 „Extern angefragt"). */
export const requestNoteBodySchema = z.object({
    requestedOn: dayString,
    offersExpectedOn: dayString,
    where: text(200).nullable().optional(),
    note: text(2000).nullable().optional(),
});
/** POST /financings/:id/offers/:offerId/confirm (06 „Bestätigen und übernehmen", 10 „Übernehmen"). */
export const confirmOfferBodySchema = z.object({
    bank: requiredText(200),
    nominalRatePct: ratePct,
    fixedInterestYears: years,
    expiresOn: dayString,
    loanCents: cents.nullable().optional(),
    effectiveRatePct: ratePct.nullable().optional(),
    installmentCents: cents.nullable().optional(),
    repaymentPct: ratePct.nullable().optional(),
    availableFromOn: dayString.nullable().optional(),
    forwardPremiumPct: ratePct.nullable().optional(),
});
/** POST /financings/:id/offers/rejection — a bank's refusal without an offer PDF (FZ-08 rows). */
export const recordRejectionBodySchema = z.object({
    bank: requiredText(200),
    rejectedOn: dayString,
    reason: requiredText(500),
    requestedOn: dayString.nullable().optional(),
});
/** POST /financings/:id/offers/:offerId/reject. */
export const rejectOfferBodySchema = z.object({
    reason: requiredText(500),
    rejectedOn: dayString.optional(),
});
/** POST /financings/:id/offers/:offerId/choose (FI11): „Der Kunde hat diesem Angebot zugestimmt." is mandatory. */
export const chooseOfferBodySchema = z.object({ customerConsented: z.boolean({ error: REQUIRED }) });
/** Label of the mandatory consent checkbox of the choose dialog. */
export const CUSTOMER_CONSENT_LABEL = 'Der Kunde hat diesem Angebot zugestimmt.';
/** POST /financings/:id/signature (FZ-07). */
export const recordSignatureBodySchema = z.object({
    signedOn: dayString,
    bank: requiredText(200),
    method: z.enum(signatureMethods, { error: 'Bitte wählen Sie, wie unterschrieben wurde.' }),
});
/** POST /financings/:id/suggest-unit (FZ-08 „Berater vorschlagen"). */
export const suggestUnitBodySchema = z.object({
    unitId: z.string({ error: REQUIRED }).uuid('Unbekannte Einheit.'),
});
//# sourceMappingURL=financier.js.map